import {ArrowBack, Edit, NavigateBefore, NavigateNext, Refresh} from '@mui/icons-material';
import {Button, CircularProgress, Stack, Typography} from '@mui/material';
import {useNavigate} from 'react-router';
import {Question} from '@cp/model';
import {QuestionFiltersSettings} from '../../../components/QuestionFilters/QuestionFiltersSettings';
import PageHeader from '../../../components/PageHeader';

export interface AdminQuestionReviewQuestionPageHeaderProps {
  onBack: () => void;
  onReloadQuestion?: () => void;
  onNextQuestion?: () => void;
  onPrevQuestion?: () => void;
  reloadLoading?: boolean;
  questionId?: string;
  moduleId?: string;
  question?: Question | null;
  filters?: QuestionFiltersSettings;
  isAdmin: boolean;
  skipCount?: number;
}

export default function AdminQuestionReviewQuestionPageHeader({
  onBack,
  onReloadQuestion,
  onNextQuestion,
  onPrevQuestion,
  reloadLoading = false,
  questionId,
  moduleId,
  question,
  filters,
  isAdmin,
  skipCount = 0,
}: AdminQuestionReviewQuestionPageHeaderProps) {
  const navigate = useNavigate();

  const handleEditQuestion = () => {
    if (questionId && moduleId && question) {
      navigate(`/admin/question/${questionId}?moduleId=${moduleId}`, {
        state: {
          question,
          filters:
            filters ||
            ({
              moduleId,
              topic: question.moduleTopic.topic,
            } as QuestionFiltersSettings),
        },
      });
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{mb: 3}}>
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{
          color: '#1B263B',
          '&:hover': {bgcolor: 'rgba(27, 38, 59, 0.04)'},
        }}
      >
        Back to Question Review
      </Button>
      <PageHeader title="Question Review" />
      {skipCount > 0 && (
        <Typography variant="body2" sx={{color: '#1B263B', mr: 1}}>
          Skip: {skipCount}
        </Typography>
      )}
      {onPrevQuestion && (
        <Button
          variant="outlined"
          startIcon={<NavigateBefore />}
          onClick={onPrevQuestion}
          disabled={reloadLoading || skipCount === 0}
          sx={{
            borderColor: '#1B263B',
            color: '#1B263B',
            '&:hover': {borderColor: '#1B263B', bgcolor: 'rgba(27, 38, 59, 0.04)'},
            minWidth: 'auto',
          }}
        >
          Prev
        </Button>
      )}
      {onNextQuestion && (
        <Button
          variant="outlined"
          endIcon={<NavigateNext />}
          onClick={onNextQuestion}
          disabled={reloadLoading}
          sx={{
            borderColor: '#1B263B',
            color: '#1B263B',
            '&:hover': {borderColor: '#1B263B', bgcolor: 'rgba(27, 38, 59, 0.04)'},
            minWidth: 'auto',
          }}
        >
          Next
        </Button>
      )}
      {onReloadQuestion && (
        <Button
          variant="outlined"
          startIcon={reloadLoading ? <CircularProgress size={20} /> : <Refresh />}
          onClick={onReloadQuestion}
          disabled={reloadLoading}
          sx={{
            borderColor: '#1B263B',
            color: '#1B263B',
            '&:hover': {borderColor: '#1B263B', bgcolor: 'rgba(27, 38, 59, 0.04)'},
          }}
        >
          Reload Question
        </Button>
      )}
      {isAdmin && questionId && moduleId && question && (
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={handleEditQuestion}
          sx={{
            bgcolor: '#FF6F59',
            '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
            color: 'white',
            fontWeight: 600,
          }}
        >
          Edit Question
        </Button>
      )}
    </Stack>
  );
}
